import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { configControllerGetPublicTenantConfigQueryOptions } from '../../generated/kubb';
import type { KorintModulesProductsConfig } from '../KorintConfig';
import { useKorintConfig } from './useKorintConfig';

export type UseKorintProductConfigResult = {
  productsConfig: Record<string, KorintModulesProductsConfig>;
  isLoading: boolean;
  isError: boolean;
};

const PRODUCTS_CONFIG_STALE_TIME = 5 * 60 * 1000;

function useBundledProductsConfig(): Record<string, KorintModulesProductsConfig> {
  const { korintConfig } = useKorintConfig();

  if (!korintConfig.productsConfig) {
    throw new Error('[{AllProducts}] Korint products config not found');
  }

  return korintConfig.productsConfig;
}

function filterToAvailable(
  productsConfig: Record<string, KorintModulesProductsConfig>,
  availableProductIds: string[] | undefined,
): Record<string, KorintModulesProductsConfig> {
  if (!availableProductIds) {
    return {};
  }

  return Object.fromEntries(
    availableProductIds
      .map(id => [id, productsConfig[id]] as const)
      .filter(([, config]) => config !== undefined),
  );
}

/**
 * Product config for the tenant's active products, with the tenant list loading state.
 * Consumers that render must gate on `isLoading`/`isError` — the map is empty until the
 * tenant config resolves.
 */
export function useKorintProductConfig(): UseKorintProductConfigResult {
  const productsConfig = useBundledProductsConfig();

  const { data, isLoading, isError } = useQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    staleTime: PRODUCTS_CONFIG_STALE_TIME,
  });

  return {
    productsConfig: filterToAvailable(productsConfig, data?.availableProductIds),
    isLoading,
    isError,
  };
}

/**
 * Suspense variant for callers with a non-null contract (e.g. `usePolicyProductConfig`):
 * suspends until the tenant config resolves, so the returned map is always populated and
 * lookups never see an empty map mid-load. Only safe under a Suspense boundary.
 */
export function useKorintProductConfigSuspense(): Record<string, KorintModulesProductsConfig> {
  const productsConfig = useBundledProductsConfig();

  const { data } = useSuspenseQuery({
    ...configControllerGetPublicTenantConfigQueryOptions(),
    staleTime: PRODUCTS_CONFIG_STALE_TIME,
  });

  return filterToAvailable(productsConfig, data.availableProductIds);
}
